import type { Signal } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { AbilityGuardDirective } from '@core/abilities-legacy';
import { Ability } from '@core/abilities-types';
import type { User } from '@core/account-types';
import { AnonymousCandidateSelectors } from '@core/candidate-data';
import type { Candidate } from '@core/candidate-types';
import {
  AnonymousInfoBarComponent,
  AnonymousRevealedForComponent,
  RevealButtonComponent,
} from '@core/candidate-ui';
import { I18nService } from '@core/i18n-legacy';
import type { Stage } from '@core/stages-types';

import { CandidateDataVisibilityService } from '../../services/candidate-data-visibility.service';
import { CandidateDataVisibilityAnalyticsDirective } from '../candidate-data-visibility-analytics.directive';

@Component({
  selector: 'rt-candidate-data-visibility-bar',
  templateUrl: './candidate-data-visibility-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AbilityGuardDirective,
    AnonymousInfoBarComponent,
    RevealButtonComponent,
    CandidateDataVisibilityAnalyticsDirective,
    AnonymousRevealedForComponent,
  ],
})
export class CandidateDataVisibilityBarComponent {
  private readonly store = inject(Store);

  private readonly i18n = inject(I18nService);

  protected readonly candidateDataVisibilityService = inject(CandidateDataVisibilityService);

  protected readonly Ability = Ability;

  protected readonly firstAnonymousCandidateStage: Signal<Stage | null> = computed(
    () =>
      this.store.selectSignal(
        AnonymousCandidateSelectors.stages({ candidateId: this.candidate().id }),
      )()[0],
  );

  protected readonly everRevealedFor: Signal<User[]> = computed(() =>
    this.store.selectSignal(
      AnonymousCandidateSelectors.everRevealedFor({ candidateId: this.candidate().id }),
    )(),
  );

  public readonly candidate = input.required<Candidate>();

  public readonly isMultiViewActive = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });

  public readonly hasBorderRadius = input<boolean, boolean | string>(true, {
    transform: booleanAttribute,
  });
}
